/**
 * 题3 sh2025q24 — 2025 上海中考 Q24「双抛物线直角梯形」。
 *
 * 主线 y = x² + bx + c 过 A(1,1)、B(3,1) → y = (x-2)²，顶点 P(2,0)，C(0,4)。
 * 副线 y = ax² + mx + n（a≠1）也过 A、B → y = a(x-2)² + 1-a，顶点 Q(2,1-a)，
 * D(0,3a+1)。四边形 CDPQ 为直角梯形时分两种情形。
 *
 * 全部由公式推导：b、c 由 y-1=(x-1)(x-3)；副线系数 m=-4a、n=1+3a；
 * CD=|3a-3|、PQ=|a-1|；两情形分别由 D(0,0)、Q(2,4) 反解 a；正弦值由
 * 垂线段 / 斜边。
 */

import { dist, dot, evalQuad, pt, sub, vertexOf } from "../math/func";
import type { Pt, Quad } from "../math/func";
import type { ProblemDef, SceneDef } from "./types";

const A = pt(1, 1);
const B = pt(3, 1);

/* (1) y-1 = (x-1)(x-3) = x²-4x+3 → y = x²-4x+4 = (x-2)²。 */
const bMain = -(A.x + B.x); // -4
const cMain = 1 + A.x * B.x; // 4

/** 主线 y = (x-2)²。 */
export const parabolaMain25: Quad = { a: 1, b: bMain, c: cMain };
const P = vertexOf(parabolaMain25); // (2, 0)
const C = pt(0, evalQuad(parabolaMain25, 0)); // (0, 4)

/* (2) 副线：m = -4a，n = 1+3a，即 y = a(x-2)² + 1-a。 */
/** 副线 y = ax² - 4a x + (3a+1)。 */
export function parabolaSub25(a: number): Quad {
  return { a, b: -4 * a, c: 1 + 3 * a };
}

/** 副线顶点 Q(2, 1-a)。 */
function vertexQ(a: number): Pt {
  return vertexOf(parabolaSub25(a));
}

/** 副线与 y 轴交点 D(0, 3a+1)。 */
function interceptD(a: number): Pt {
  return pt(0, evalQuad(parabolaSub25(a), 0));
}

/* Scene ①：取 a=-1 作示意图。 */
const aDemo = -1;
const Q1 = vertexQ(aDemo); // (2, 2)
const D1 = interceptD(aDemo); // (0, -2)
const cdLen = Math.abs(D1.y - C.y); // |3a-3|
const pqLen = Math.abs(Q1.y - P.y); // |a-1|
const ratio = cdLen / pqLen; // 3

/* Scene ②：情形一 CD⊥PD → D 在 x 轴上 → 3a+1=0 → a=-1/3。 */
const aCase1 = (0 - 1) / 3; // -1/3（由 3a+1=0）
const Q2 = vertexQ(aCase1); // (2, 4/3)
const D2 = interceptD(aCase1); // (0, 0)
const M = pt(0, Q2.y); // QM⊥CD 于 M(0, 4/3)
const qmLen = dist(Q2, M); // 2
const cmLen = dist(C, M); // 8/3
const cqLen = dist(C, Q2); // 10/3
const sinQCD = qmLen / cqLen; // 3/5

/* Scene ③：情形二 CD⊥CQ → Q 与 C 同高 → 1-a=4 → a=-3。 */
const aCase2 = 1 - C.y; // -3（由 1-a=4）
const Q3 = vertexQ(aCase2); // (2, 4)
const D3 = interceptD(aCase2); // (0, -8)
const N = pt(0, P.y); // PN⊥CD 于 N(0, 0)
const pnLen = dist(P, N); // 2
const dnLen = dist(D3, N); // 8
const pdLen = dist(P, D3); // 2√17
const sinCDP = pnLen / pdLen; // √17/17

const scene1: SceneDef = {
  id: "s1",
  tab: "① CD/PQ = 3",
  caption:
    "主线 y=(x-2)²：P(2,0)、C(0,4)。副线 y=a(x-2)²+1-a：Q(2,1-a)、D(0,3a+1)。" +
    "CD=|3a-3|、PQ=|a-1|，比值恒为 3。图中取 a=-1（D(0,-2)、Q(2,2)）。",
  view: { xMin: -2.5, xMax: 5, yMin: -3, yMax: 5.5 },
  curves: [
    { kind: "quad", id: "main", quad: parabolaMain25, tone: "cyan" },
    { kind: "quad", id: "sub", quad: parabolaSub25(aDemo), tone: "green" },
    { kind: "vline", id: "axis", x: P.x, tone: "dim", dashed: true },
  ],
  points: [
    { name: "A", at: A, showCoords: true, dx: -4, dy: 16 },
    { name: "B", at: B, showCoords: true, dx: 6, dy: 16 },
    { name: "C", at: C, showCoords: true, dx: -16, dy: -6 },
    { name: "P", at: P, dx: 12, dy: 14 },
    { name: "Q", at: Q1, emphasis: true, dx: 12, dy: -8 },
    { name: "D", at: D1, emphasis: true, dx: -16, dy: 10 },
  ],
  segments: [
    { a: C, b: D1, tone: "gold", dashed: true },
    { a: P, b: Q1, tone: "gold", dashed: true },
  ],
};

const scene2: SceneDef = {
  id: "s2",
  tab: "② 情形一 CD⊥PD",
  caption:
    "CD⊥PD 时 D 落在 x 轴上：3a+1=0 → a=-1/3，Q(2,4/3)。∠QCD 为最小内角。" +
    "作 QM⊥CD 于 M(0,4/3)：QM=2，CM=8/3，CQ=10/3，sin∠QCD=3/5。",
  view: { xMin: -1.5, xMax: 4, yMin: -1.5, yMax: 5 },
  curves: [
    { kind: "quad", id: "main", quad: parabolaMain25, tone: "cyan" },
    { kind: "quad", id: "sub", quad: parabolaSub25(aCase1), tone: "green" },
    { kind: "vline", id: "axis", x: P.x, tone: "dim", dashed: true },
  ],
  points: [
    { name: "A", at: A, tone: "dim", dx: -4, dy: 14 },
    { name: "B", at: B, tone: "dim", dx: 8, dy: 10 },
    { name: "C", at: C, showCoords: true, dx: -16, dy: -6 },
    { name: "D", at: D2, showCoords: true, dx: -18, dy: 14 },
    { name: "P", at: P, dx: 12, dy: 14 },
    { name: "Q", at: Q2, emphasis: true, dx: 12, dy: -8 },
    { name: "M", at: M, dx: -16, dy: -8 },
  ],
  fills: [{ points: [C, D2, P, Q2], tone: "gold" }],
  segments: [{ a: Q2, b: M, tone: "gold", dashed: true }],
  rightAngles: [
    { at: D2, from: C, to: P },
    { at: M, from: Q2, to: C },
  ],
};

const scene3: SceneDef = {
  id: "s3",
  tab: "③ 情形二 CD⊥CQ",
  caption:
    "CD⊥CQ 时 Q 与 C 同高：1-a=4 → a=-3，D(0,-8)。∠CDP 为最小内角。" +
    "作 PN⊥CD 于 N(0,0)：PN=2，DN=8，PD=2√17，sin∠CDP=√17/17。",
  view: { xMin: -1.5, xMax: 4, yMin: -9.5, yMax: 5.5 },
  curves: [
    { kind: "quad", id: "main", quad: parabolaMain25, tone: "cyan" },
    { kind: "quad", id: "sub", quad: parabolaSub25(aCase2), tone: "green" },
    { kind: "vline", id: "axis", x: P.x, tone: "dim", dashed: true },
  ],
  points: [
    { name: "A", at: A, tone: "dim", dx: -4, dy: 14 },
    { name: "B", at: B, tone: "dim", dx: 8, dy: 10 },
    { name: "C", at: C, showCoords: true, dx: -16, dy: -8 },
    { name: "D", at: D3, showCoords: true, dx: -18, dy: 12 },
    { name: "P", at: P, dx: 12, dy: 6 },
    { name: "Q", at: Q3, emphasis: true, dx: 12, dy: -8 },
    { name: "N", at: N, dx: 12, dy: 16 },
  ],
  fills: [{ points: [C, D3, P, Q3], tone: "gold" }],
  segments: [{ a: P, b: N, tone: "gold", dashed: true }],
  rightAngles: [
    { at: N, from: P, to: D3 },
    { at: C, from: D3, to: Q3 },
  ],
};

export const sh2025q24: ProblemDef = {
  id: "sh2025q24",
  title: "2025·Q24 双抛物线直角梯形",
  statement:
    "抛物线 y=x²+bx+c 经过 A(1,1)、B(3,1)，顶点为 P，与 y 轴交于 C。（1）求 b、c；" +
    "（2）另一条抛物线 y=ax²+mx+n（a≠1）也经过 A、B，顶点为 Q，与 y 轴交于 D。" +
    "① 求 CD/PQ 的值；② 当四边形 CDPQ 是直角梯形时，求其最小内角的正弦值。",
  givens: [
    { label: "A", value: "(1, 1)" },
    { label: "B", value: "(3, 1)" },
    { label: "副线", value: "a ≠ 1" },
  ],
  scenes: [scene1, scene2, scene3],
  solution: [
    "（1）将 A(1,1)、B(3,1) 代入 y=x²+bx+c，得 b=-4，c=4，即 y=(x-2)²，" +
      "顶点 P(2,0)，C(0,4)。",
    "（2）① 将 A、B 代入 y=ax²+mx+n，得 m=-4a，n=1+3a，即 y=a(x-2)²+1-a，" +
      "Q(2,1-a)，D(0,3a+1)。CD=|3a-3|，PQ=|a-1|，故 CD/PQ=3。",
    "② 情形一 CD⊥PD：D(0,0) → 3a+1=0 → a=-1/3，Q(2,4/3)，∠QCD 为最小内角。" +
      "作 QM⊥CD 于 M(0,4/3)：QM=2，CM=8/3，CQ=10/3，sin∠QCD=3/5。",
    "情形二 CD⊥CQ：Q(2,4) → 1-a=4 → a=-3，D(0,-8)，∠CDP 为最小内角。" +
      "作 PN⊥CD 于 N(0,0)：PN=2，DN=8，PD=2√17，sin∠CDP=√17/17。" +
      "综上，最小内角的正弦值为 3/5 或 √17/17。",
  ],
  answer: "b=-4, c=4 ｜ CD/PQ=3 ｜ sin = 3/5 或 √17/17",
  computed: {
    b: bMain,
    c: cMain,
    ratio,
    aCase1,
    qm: qmLen,
    cm: cmLen,
    cq: cqLen,
    sinQCD,
    aCase2,
    pn: pnLen,
    dn: dnLen,
    pd: pdLen,
    sinCDP,
    case1PerpDot: dot(sub(D2, C), sub(D2, P)),
    case2PerpDot: dot(sub(C, D3), sub(C, Q3)),
  },
};
